import { useEffect, useRef, type ComponentType } from 'react';
import { Navigate, useLocation, useMatch } from 'react-router-dom';
import { isAxiosError } from 'axios';

import {
  getRoutePath,
  getRoutePattern,
  ONBOARDING_STEPS,
  PARENT_ROUTES,
} from '@/routes/util';
import { FullPageLoader } from '@/shared/components';
import { useCurrentUser } from '@/generated/hooks/agora/v1/user';
import { useGetWorkspace } from '@/generated/hooks/agora/v1/workspace';
import { useIsFinanceWorkspace } from '@/shared/hooks/use-is-finance-workspace';
import { usePermissions, usePermissionsIndex } from '@/shared/permissions';
import { useListAutomationSummaries } from '@/generated/hooks/grimoire/v1/api';
import { Sentry } from '@/modules/sentry';
import { useStatsigUser, useStatsigClient } from '@statsig/react-bindings';

import { useAuth } from '../hooks/use-auth';
import { ErrorScreen } from './ErrorScreen';
import {
  selectCurrentOrganizationId,
  selectSetCurrentOrganizationId,
  selectSetCurrentWorkspaceId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { V1OnboardingState } from '@/generated/hooks/agora/models';
import {
  checkIsAccessDeniedError,
  checkIsNotFoundError,
} from '@/shared/utils/error-handler';
import {
  checkHasNoUserRecord,
  checkIsNewUser,
  FilterBuilder,
  parseResourceName,
} from '@/shared/utils';
import { useIsKognitosEmployee } from '@/shared/hooks';

/**
 * Higher-Order Component that wraps a component with authentication protection.
 *
 * This HOC performs the following checks:
 * 1. Verifies Auth0 authentication status
 * 2. Checks if user exists in backend database via /api/v1/me
 * 3. Redirects to /onboarding if authenticated but user not found in backend (403)
 * 4. Shows error screen with retry for other backend failures
 *
 * Usage:
 * ```tsx
 * import { withProtectedRoute } from '@/shared/auth';
 *
 * const MyComponent = () => <div>Protected Content</div>;
 * const ProtectedMyComponent = withProtectedRoute(MyComponent);
 *
 * // In your router or JSX
 * <ProtectedMyComponent />
 * ```
 */
export const withProtectedRoute = <P extends object>(
  Component: ComponentType<P>,
) => {
  return (props: P) => {
    const {
      user,
      isAuthenticated,
      isLoading: isAuthLoading,
      logout,
    } = useAuth();

    const isKognitosEmployee = useIsKognitosEmployee();

    const { updateUserAsync } = useStatsigUser();
    const { client } = useStatsigClient();
    const location = useLocation();
    const hasTriggeredTokenErrorLogoutRef = useRef(false);
    const setCurrentOrganizationId = useUserPreferencesStore(
      selectSetCurrentOrganizationId,
    );
    const currentOrganizationId = useUserPreferencesStore(
      selectCurrentOrganizationId,
    );
    const setCurrentWorkspaceId = useUserPreferencesStore(
      selectSetCurrentWorkspaceId,
    );
    const currentWorkspaceId = useUserPreferencesStore(
      selectCurrentWorkspaceId,
    );

    const onboardingMatch = useMatch(getRoutePattern('ONBOARDING'));
    const isOnboardingPage = !!onboardingMatch;

    const isAcceptInvitationPage = Boolean(
      useMatch(getRoutePattern('INVITATION')),
    );

    // Match workspace routes to extract orgId and workspaceId
    const workspaceRouteMatch = useMatch(`${PARENT_ROUTES.WORKSPACE}/*`);
    const isWorkspaceRoute = Boolean(workspaceRouteMatch);
    const urlOrgId = workspaceRouteMatch?.params.orgId;
    const urlWorkspaceId = workspaceRouteMatch?.params.workspaceId;
    const workspaceRootMatch = useMatch(PARENT_ROUTES.WORKSPACE);
    const isWorkspaceRootPath = Boolean(workspaceRootMatch);

    // Check if user is on the access denied page (to prevent infinite redirect loop)
    const isWorkspaceAccessDeniedPage = Boolean(
      useMatch(getRoutePattern('WORKSPACE_ACCESS_DENIED')),
    );

    const {
      isLoading: isUserLoading,
      error: currentUserError,
      data: currentUser,
    } = useCurrentUser({
      query: {
        enabled: isAuthenticated,
        retry: false,
      },
    });

    // Fetch user's permissions across all orgs and workspaces and build the
    // O(1) lookup index that downstream RBAC checks consume.
    const { isLoading: isPermissionsLoading, error: permissionsError } =
      usePermissionsIndex({ enabled: isAuthenticated });
    // 403 on /me/permissions is expected for new users mid-onboarding (no role
    // assigned yet). Don't promote it to ErrorScreen — `shouldRedirectToOnboarding`
    // below handles them.
    const isPermissionsError =
      isAxiosError(permissionsError) &&
      permissionsError.response?.status !== 403;

    // Check workspace access when on workspace routes (except access denied page)
    const shouldCheckWorkspaceAccess = Boolean(
      isAuthenticated &&
        currentUser?.data?.user &&
        isWorkspaceRoute &&
        !isWorkspaceAccessDeniedPage &&
        urlWorkspaceId,
    );

    const { isLoading: isWorkspaceLoading, error: workspaceError } =
      useGetWorkspace(urlOrgId ?? '', urlWorkspaceId ?? '', {
        query: {
          enabled: shouldCheckWorkspaceAccess,
          retry: false,
        },
      });

    const isWorkspaceAccessDenied = checkIsAccessDeniedError(workspaceError);
    const isWorkspaceNotFound = checkIsNotFoundError(workspaceError);

    const lastOrg = currentUser?.data.user?.last_org;
    const lastWorkspace = currentUser?.data.user?.last_workspace;

    // eslint-disable-next-line no-restricted-syntax -- reason: Sync last org/workspace from user data to store
    useEffect(() => {
      if (lastOrg && !currentOrganizationId) {
        setCurrentOrganizationId(lastOrg);
      }
      if (
        lastWorkspace &&
        !currentWorkspaceId &&
        (!currentOrganizationId || currentOrganizationId === lastOrg)
      ) {
        setCurrentWorkspaceId(lastWorkspace);
      }
    }, [
      currentOrganizationId,
      currentWorkspaceId,
      lastOrg,
      lastWorkspace,
      setCurrentOrganizationId,
      setCurrentWorkspaceId,
    ]);

    // Extract user values for stable comparison
    const userName = currentUser?.data?.user?.name;

    // eslint-disable-next-line no-restricted-syntax -- reason: Sync Sentry identity + tenant attributes from user data so every error/log carries org/workspace context
    useEffect(() => {
      const email = currentUser?.data?.user?.email;

      if (!email) {
        Sentry.setUser(null);
        Sentry.getIsolationScope().setAttributes({});
        return;
      }

      const userId = userName ? parseResourceName(userName).user : undefined;
      Sentry.setUser({ id: userId, email });

      const attrs: Record<string, string> = {};
      if (currentOrganizationId) attrs['org.id'] = currentOrganizationId;
      if (currentWorkspaceId) attrs['workspace.id'] = currentWorkspaceId;

      Sentry.getIsolationScope().setAttributes(attrs);
    }, [
      currentOrganizationId,
      currentUser?.data?.user?.email,
      currentWorkspaceId,
      userName,
    ]);

    // eslint-disable-next-line no-restricted-syntax -- reason: Update Statsig user once currentUser data is fetched for feature flags and analytics
    useEffect(() => {
      if (!client || !userName) {
        return;
      }

      const userId = parseResourceName(userName).user;

      // Get current Statsig context to check if user is already set
      const context = client.getContext();
      const currentStatsigUserId = context?.user?.userID;
      const currentStatsigWorkspaceId = context?.user?.custom?.workspaceId;
      const currentStatsigOrganizationId =
        context?.user?.custom?.organizationId;

      // Only update if the userId doesn't match what's already in Statsig or if the workspaceId or organizationId doesn't match what's already in Statsig
      if (
        currentStatsigUserId !== userId ||
        currentStatsigWorkspaceId !== currentWorkspaceId ||
        currentStatsigOrganizationId !== currentOrganizationId
      ) {
        updateUserAsync({
          userID: userId,
          custom: {
            workspaceId: currentWorkspaceId,
            organizationId: currentOrganizationId,
            isKognitosEmployee,
          },
        });
      }
    }, [
      userName,
      client,
      currentWorkspaceId,
      currentOrganizationId,
      updateUserAsync,
      isKognitosEmployee,
    ]);

    const isOnboardingCompleted =
      currentUser?.data?.user?.onboarding_state === V1OnboardingState.COMPLETED;

    const workspaceCreationPending =
      currentUser?.data?.user?.onboarding_state ===
      V1OnboardingState.WORKSPACE_CREATION_PENDING;

    const isNewUser = checkIsNewUser(currentUser?.data?.user, user);

    // Check if user data is missing (403 forbidden error)
    const isUserDataMissing = checkHasNoUserRecord(currentUserError);

    // Check if backend returned an error (non-403 status)
    const isBackendError = isAxiosError(currentUserError) && !isUserDataMissing;

    // Check if the error is a token fetch failure (e.g. stale token, expired refresh token).
    const isTokenFetchError =
      !!currentUserError && !isAxiosError(currentUserError);

    // eslint-disable-next-line no-restricted-syntax -- reason: Logout is an imperative auth side effect that must run outside render
    useEffect(() => {
      if (!isTokenFetchError || hasTriggeredTokenErrorLogoutRef.current) {
        return;
      }

      hasTriggeredTokenErrorLogoutRef.current = true;
      logout();
    }, [isTokenFetchError, logout]);

    // If user data is missing - onboarding is not started yet
    // If onboarding started and not completed - redirect to onboarding workspace step
    // Exception: Allow invitation page to render even if user is not onboarded
    // Only check onboarding state when we have actual user data from the backend,
    // to avoid flashing the onboarding screen when a stale token causes a fetch error.
    const shouldRedirectToOnboarding =
      !isOnboardingPage &&
      !isAcceptInvitationPage &&
      (isUserDataMissing || (!!currentUser && !isOnboardingCompleted));

    // Prefer URL params (available on workspace-root visits before the
    // URL→store sync in MainLayout runs); fall back to store for HOME.
    const landingOrgId =
      urlOrgId ?? workspaceRootMatch?.params.orgId ?? currentOrganizationId;
    const landingWorkspaceId =
      urlWorkspaceId ??
      workspaceRootMatch?.params.workspaceId ??
      currentWorkspaceId;

    const isHomePath = location.pathname === getRoutePath('HOME');
    const shouldRedirectFromHome = Boolean(
      landingOrgId &&
        landingWorkspaceId &&
        isOnboardingCompleted &&
        (isHomePath || isWorkspaceRootPath),
    );

    // Users without ListAutomations land on Integrations; skip probing the
    // automations endpoint (which would 403).
    const { hasPermission } = usePermissions();
    const canListAutomations = hasPermission(['ListAutomations'], {
      organization: landingOrgId ?? '',
      workspace: landingWorkspaceId ?? '',
    });

    // Workspaces with no published automations land on drafts; everything
    // else on automations. Fires for HOME and workspace-root visits.
    const {
      data: homeAutomationsData,
      isLoading: isHomeAutomationsLoading,
      isError: isHomeAutomationsError,
    } = useListAutomationSummaries(
      landingOrgId ?? '',
      landingWorkspaceId ?? '',
      {
        page_size: 1,
        filter: FilterBuilder.create().equals('stage', 'PUBLISHED').build(),
      },
      {
        query: {
          enabled: shouldRedirectFromHome && canListAutomations,
          staleTime: 5 * 60 * 1000,
        },
      },
    );

    // The finance experience (landing + downstream pages) is gated behind the
    // `enable_finance_workspace` Statsig flag AND restricted to the finance-typed
    // workspace. Both conditions live in useIsFinanceWorkspace — the single
    // source of truth shared with the route guard (FinanceLayout) and the
    // sidebar — so the gate can't be forgotten at this redirect boundary.
    // Resolve the landing workspace (only while redirecting from home, via the
    // `enabled` option) to decide whether it lands on its finance cockpit;
    // every other workspace keeps the automations/drafts default. On a transient
    // fetch error the hook reports isFinanceWorkspace=false, so we never redirect
    // to finance on a guess.
    const {
      isFinanceWorkspace: isFinanceLanding,
      isLoading: isLandingWorkspaceLoading,
    } = useIsFinanceWorkspace(landingOrgId, landingWorkspaceId, {
      enabled: shouldRedirectFromHome,
    });

    const isUserDataPending =
      isAuthenticated && !currentUser && !currentUserError;
    // Should show loading screen if Auth0 is still loading, user data is loading,
    // workspace access is being checked, or a token fetch error occurred (Auth0 will
    // update isAuthenticated to false shortly, triggering a redirect to login).
    // Trade-off: from-home redirect waits on BOTH the automations list and the
    // finance workspace lookup (useIsFinanceWorkspace). They are enabled together
    // so they run in parallel — latency is max(automations, workspace), not the
    // sum. Both are required up front: the finance check needs the workspace
    // type, and the automations/drafts fallback needs the automations count, and
    // either can be the redirect target. For non-finance users the workspace
    // result is then discarded, so they pay whatever the workspace call costs
    // beyond the automations call. This only affects the from-home path, not
    // normal nav.
    const shouldShowLoadingScreen =
      isAuthLoading ||
      isUserLoading ||
      isUserDataPending ||
      isPermissionsLoading ||
      isTokenFetchError ||
      (shouldCheckWorkspaceAccess && isWorkspaceLoading) ||
      (shouldRedirectFromHome && isHomeAutomationsLoading) ||
      (shouldRedirectFromHome && isLandingWorkspaceLoading);

    // If user is not authenticated and Auth0 is not loading, redirect to login
    const shouldRedirectToLogin = !isAuthenticated && !isAuthLoading;

    // Redirect to workspace access denied page if user doesn't have access to the workspace
    const shouldRedirectToWorkspaceAccessDenied =
      isWorkspaceAccessDenied &&
      urlOrgId &&
      urlWorkspaceId &&
      !isWorkspaceAccessDeniedPage;

    // Redirect to not-found page when the workspace ID in the URL doesn't exist
    const shouldRedirectToWorkspaceNotFound =
      isWorkspaceNotFound && urlWorkspaceId && !isWorkspaceAccessDeniedPage;

    if (shouldRedirectToLogin) {
      return (
        <Navigate
          to={getRoutePath('LOGIN')}
          replace
          state={{ from: location.pathname + location.search }}
        />
      );
    }

    if (isBackendError || isPermissionsError) {
      return <ErrorScreen />;
    }

    if (shouldShowLoadingScreen) {
      return <FullPageLoader />;
    }

    if (shouldRedirectToOnboarding) {
      // Determine the starting onboarding step based on different conditions.
      const getOnboardingStep = () => {
        if (workspaceCreationPending) {
          return ONBOARDING_STEPS.WORKSPACE;
        }
        if (isNewUser) {
          return ONBOARDING_STEPS.USER_INFORMATION;
        }
        return ONBOARDING_STEPS.ORGANIZATION;
      };

      return (
        <Navigate
          to={getRoutePath('ONBOARDING', {
            onboardingStep: getOnboardingStep(),
          })}
          replace
          state={{ isNewUser }}
        />
      );
    }

    if (shouldRedirectToWorkspaceAccessDenied) {
      return (
        <Navigate
          to={getRoutePath('WORKSPACE_ACCESS_DENIED', {
            orgId: urlOrgId,
            workspaceId: urlWorkspaceId,
          })}
          replace
        />
      );
    }

    if (shouldRedirectToWorkspaceNotFound) {
      return <Navigate to={getRoutePath('NOT_FOUND')} replace />;
    }

    if (shouldRedirectFromHome && landingOrgId && landingWorkspaceId) {
      if (!canListAutomations) {
        return (
          <Navigate
            to={getRoutePath('INTEGRATIONS', {
              orgId: landingOrgId,
              workspaceId: landingWorkspaceId,
            })}
            replace
          />
        );
      }
      // The finance workspace lands on its cockpit — the primary finance surface.
      if (isFinanceLanding) {
        return (
          <Navigate
            to={getRoutePath('FINANCE_COCKPIT', {
              orgId: landingOrgId,
              workspaceId: landingWorkspaceId,
            })}
            replace
          />
        );
      }
      const hasAutomations =
        (homeAutomationsData?.data?.automation_summaries?.length ?? 0) > 0;
      // On fetch error, fall back to automations (prior default).
      const target =
        isHomeAutomationsError || hasAutomations ? 'AUTOMATIONS' : 'DRAFTS';
      return (
        <Navigate
          to={getRoutePath(target, {
            orgId: landingOrgId,
            workspaceId: landingWorkspaceId,
          })}
          replace
        />
      );
    }

    // User is authenticated and verified, render the protected component
    return <Component {...props} />;
  };
};
